import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Box,
  Display,
  DisplayPosition,
  Modal,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Overflow,
  ScreenReaderLiveRegion,
  VisuallyHidden,
  ZIndex,
  useModal,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'
import { useDebounce } from 'react-use'

import {
  FlexModalOverlay,
  FlexPortionFooter,
  FlexPortionSummary,
  FlexPortionTileBadge,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type { RecipeURN, SearchStandardRecipeItem } from '@library/api-menu-experience'
import { FlexPortionEditorBoundary } from '@library/menu-page'
import type {
  CollectionSlug,
  CollectionUUID,
  MenuRecipeTileRequestParams,
  RecipeUUID,
} from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { menuSearchModalOpen } from 'actions/trackingKeys'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import type { SelectCollectionEvent } from 'routes/Menu/MenuExperience/types'
import { useBasket } from 'routes/Menu/domains/basket'
import { useChangeCollectionById } from 'routes/Menu/domains/collections'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

import { RecipeDetail } from '../RecipeDetail/RecipeDetail'
import { SearchInput } from '../SearchInput/SearchInput'
import type { SearchRecipeTileTrackingData, TrackCTAClick } from '../types'
import { MenuExperienceSearchResults } from './MenuExperienceSearchResults'
import {
  getMenuExperienceSearchViewState,
  isMenuExperienceSearchBusy,
  isMenuExperienceSearchReplacing,
} from './menuExperienceSearchViewState'
import { getMenuSearchBasketRecipes, useMenuExperienceSearch } from './useMenuExperienceSearch'

import css from './styles.css'

const SearchResultsContent = withCitrusTheme(Box, () => ({
  opacity: 1,
  transition: 'opacity 160ms ease-out',

  '&[data-replacing="true"]': {
    opacity: 0.4,
    pointerEvents: 'none',
  },

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}))

type SearchResultsRegionProps = {
  children: React.ReactNode
  isBusy: boolean
  isReplacing: boolean
}

const SearchResultsRegion = ({ children, isBusy, isReplacing }: SearchResultsRegionProps) => {
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const content = contentRef.current

    if (!content) return

    if (isReplacing) {
      content.setAttribute('inert', '')
    } else {
      content.removeAttribute('inert')
    }
  }, [isReplacing])

  return (
    <>
      <ScreenReaderLiveRegion urgency="polite">
        <VisuallyHidden data-testid="search-results-loading-announcement">
          {isBusy ? 'Loading search results' : ''}
        </VisuallyHidden>
      </ScreenReaderLiveRegion>
      <Box aria-busy={isBusy || undefined} data-testid="search-results-region">
        <Box ref={contentRef} data-testid="search-results-content">
          <SearchResultsContent
            data-replacing={isReplacing}
            data-testid="search-results-visible-content"
            paddingH={2}
            paddingBottom={4}
          >
            {children}
          </SearchResultsContent>
        </Box>
      </Box>
    </>
  )
}

type ViewRecipeDetails = {
  invocationId?: string
  menuId: string
  recipeId: RecipeUUID | null
  recipeRank?: number
}

type FlexPortionEditorRecipe = {
  recipeId: RecipeUUID
  recipeUrn?: RecipeURN
  trackingData: SearchRecipeTileTrackingData
}

export const recipeSearchModalName = 'RecipeSearchModal'

export const RecipeSearchModal = () => {
  const { closeCurrentModal, getCurrentModal } = useModal()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [recipeDetailsId, setRecipeDetailsId] = useState<RecipeUUID | null>(null)
  const [flexPortionEditorRecipe, setFlexPortionEditorRecipe] =
    useState<FlexPortionEditorRecipe | null>(null)
  const searchResultsRef = useRef<HTMLDivElement>(null)
  const basketDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const basketNumPortions = useSelector(getNumPortions)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { basketHasRecipe, basketRecipes } = useBasketRecipes()
  const isSearchModalOpen = getCurrentModal() === recipeSearchModalName

  useDebounce(() => setDebouncedSearchText(searchText), 500, [searchText])

  const changeSearchText = (value: string) => {
    setSearchText(value)
    setFlexPortionEditorRecipe(null)

    if (searchResultsRef.current) {
      searchResultsRef.current.scrollTop = 0
    }
  }

  const {
    data: menuExperienceSearchData,
    error: menuExperienceSearchError,
    isLoading: isMenuExperienceSearchLoading,
    isReplacingQueryResults,
    retry: retryMenuExperienceSearch,
  } = useMenuExperienceSearch({
    isOpen: isSearchModalOpen,
    menuId: basketMenuUuid,
    query: debouncedSearchText,
    numPortions: basketNumPortions,
    deliveryDate: basketDate,
    recipes: getMenuSearchBasketRecipes(basketRecipes),
  })

  const {
    addRecipeByUuid,
    removeRecipeByUuid,
    reachedLimit,
    canAddRecipes,
    getQuantitiesForRecipeId,
    getNumPortionsForRecipeId,
    recipeCount,
    updateRecipePortionsByUuid,
  } = useBasket()
  const changeCollectionById = useChangeCollectionById()
  const cleanUpState = () => {
    setSearchText('')
    setRecipeDetailsId(null)
    setFlexPortionEditorRecipe(null)
  }

  const viewRecipeDetails = ({ invocationId, menuId, recipeId, recipeRank }: ViewRecipeDetails) => {
    setRecipeDetailsId(recipeId)
    setFlexPortionEditorRecipe(null)
    queueTrackingEvent<{
      action: 'view_recipe_details'
      properties: {
        menu_id: string
        recipe_id: string
        search_invocation_id?: string
        search_rank?: number
      }
    }>({
      action: 'view_recipe_details',
      properties: {
        menu_id: menuId,
        recipe_id: recipeId || '',
        ...(invocationId && { search_invocation_id: invocationId }),
        ...(recipeRank !== undefined && { search_rank: recipeRank }),
      },
    })
    datadogRum.addAction('open_recipe_details', {
      menu_id: menuId,
      recipe_id: recipeId || '',
      source: 'search',
    })
  }

  const viewSearch = ({ menuId }: { menuId: string }) => {
    setRecipeDetailsId(null)
    setFlexPortionEditorRecipe(null)
    queueTrackingEvent<{
      action: typeof menuSearchModalOpen
      properties: { menu_id: string }
    }>({
      action: menuSearchModalOpen,
      properties: { menu_id: menuId },
    })
    datadogRum.addAction('open_search', { menu_id: menuId })
  }

  const closeFlexPortionEditor = () => setFlexPortionEditorRecipe(null)
  const openFlexPortionEditor = (
    recipe: { id: RecipeUUID; urn?: RecipeURN },
    trackingData: SearchRecipeTileTrackingData,
  ) => setFlexPortionEditorRecipe({ recipeId: recipe.id, recipeUrn: recipe.urn, trackingData })

  const getMenuExperienceSearchTrackingData = (
    recipe: SearchStandardRecipeItem,
    invocationId?: string,
    rank?: number,
  ): TrackCTAClick['properties'] => ({
    ...(invocationId && { search_invocation_id: invocationId }),
    ...(rank !== undefined && { search_rank: rank }),
    menu_id: basketMenuUuid,
    add_type: 'search',
    event_screen: 'menu_search_screen',
    recipe_id: recipe.id,
  })

  const selectMenuExperienceSearchCategory = ({ id, slug }: { id: string; slug: string }) => {
    closeCurrentModal()
    queueTrackingEvent<SelectCollectionEvent>({
      action: 'select_collection',
      properties: {
        collection_id: id as CollectionUUID,
        collection_slug: slug as CollectionSlug,
        navigation_method: 'search',
      },
    })
    changeCollectionById(id as CollectionUUID)
  }

  const updateFlexPortionEditorRecipePortions = (updatedNumPortions: number) => {
    if (!flexPortionEditorRecipe) return

    updateRecipePortionsByUuid(
      flexPortionEditorRecipe.recipeId,
      updatedNumPortions as MenuRecipeTileRequestParams['numPortions'],
    )
    closeFlexPortionEditor()
  }

  const removeFlexPortionEditorRecipe = () => {
    if (!flexPortionEditorRecipe) return

    const { recipeId, trackingData } = flexPortionEditorRecipe
    queueTrackingEvent<TrackCTAClick>({
      action: 'remove_recipe',
      properties: {
        search_invocation_id: trackingData.invocationId,
        search_rank: trackingData.index,
        menu_id: trackingData.menuId,
        add_type: 'search',
        event_screen: 'menu_search_screen',
        recipe_id: recipeId,
      },
    })
    removeRecipeByUuid(recipeId)
    closeFlexPortionEditor()
  }

  const flexPortionEditorRecipeCount = flexPortionEditorRecipe
    ? getQuantitiesForRecipeId(flexPortionEditorRecipe.recipeId)
    : 0
  const flexPortionEditorNumPortions = flexPortionEditorRecipe
    ? getNumPortionsForRecipeId(flexPortionEditorRecipe.recipeId)
    : 0
  const flexPortionEditorTrackingContext = flexPortionEditorRecipe
    ? {
        basketRecipeCount: recipeCount,
        menuUuid: basketMenuUuid,
        recipePortionCount: flexPortionEditorNumPortions,
        recipeUuid: flexPortionEditorRecipe.recipeId,
        source: 'menu_search_screen' as const,
      }
    : undefined
  const isFlexPortionEditorOpen = Boolean(
    flexPortionEditorRecipe && flexPortionEditorRecipeCount > 0 && !recipeDetailsId,
  )
  const isMenuExperienceSearchTransitioning = Boolean(
    menuExperienceSearchData && (searchText !== debouncedSearchText || isReplacingQueryResults),
  )
  const menuExperienceSearchViewState = getMenuExperienceSearchViewState({
    error: menuExperienceSearchError,
    isLoading: isMenuExperienceSearchLoading,
    isReplacing: isMenuExperienceSearchTransitioning,
    query: debouncedSearchText,
    response: menuExperienceSearchData,
  })

  useEffect(() => {
    if (flexPortionEditorRecipe && flexPortionEditorRecipeCount === 0) {
      closeFlexPortionEditor()
    }
  }, [flexPortionEditorRecipe, flexPortionEditorRecipeCount])

  return (
    <Modal
      className={css.RecipeSearchModal}
      name={recipeSearchModalName}
      onRequestClose={closeCurrentModal}
      onAfterClose={cleanUpState}
      overlayClassName={recipeDetailsId || isFlexPortionEditorOpen ? css.overlay : ''}
    >
      {recipeDetailsId ? (
        <RecipeDetail
          onClose={() => viewSearch({ menuId: basketMenuUuid })}
          onSelectRecipe={setRecipeDetailsId}
          recipeId={recipeDetailsId}
        />
      ) : (
        <ModalHeader>
          <Box paddingRight={[4, 12]}>
            <SearchInput onChange={changeSearchText} value={searchText} />
          </Box>
          <ModalClose onClose={closeCurrentModal} />
        </ModalHeader>
      )}
      <Box
        ref={searchResultsRef}
        data-testid="search-results"
        display={recipeDetailsId ? Display.None : Display.Initial}
        overflowY={isFlexPortionEditorOpen ? Overflow.Hidden : Overflow.Scroll}
        paddingV={[4, 6]}
        paddingH={[4, 6]}
      >
        <SearchResultsRegion
          isBusy={isMenuExperienceSearchBusy(menuExperienceSearchViewState)}
          isReplacing={isMenuExperienceSearchReplacing(menuExperienceSearchViewState)}
        >
          <MenuExperienceSearchResults
            canAddRecipes={canAddRecipes}
            getIsInBasket={basketHasRecipe}
            getQuantity={getQuantitiesForRecipeId}
            isAuthenticated={isAuthenticated}
            reachedLimit={reachedLimit}
            retry={retryMenuExperienceSearch}
            viewState={menuExperienceSearchViewState}
            onAddRecipe={(recipe, context) => {
              queueTrackingEvent<TrackCTAClick>({
                action: 'add_recipe',
                properties: getMenuExperienceSearchTrackingData(
                  recipe,
                  context.invocationId,
                  context.rank,
                ),
              })
              addRecipeByUuid(recipe.id, 'menu_search_screen')
            }}
            onBlockedAddAttempt={setBrowserCTAVisible}
            onCategoryClick={selectMenuExperienceSearchCategory}
            onRecipeClick={(recipe, context) =>
              viewRecipeDetails({
                invocationId: context.invocationId,
                menuId: basketMenuUuid,
                recipeId: recipe.id,
                recipeRank: context.rank,
              })
            }
            onRemoveRecipe={(recipe, context) => {
              queueTrackingEvent<TrackCTAClick>({
                action: 'remove_recipe',
                properties: getMenuExperienceSearchTrackingData(
                  recipe,
                  context.invocationId,
                  context.rank,
                ),
              })
              removeRecipeByUuid(recipe.id)
            }}
            renderFlexPortionControl={
              isAuthenticated
                ? (recipe, context, variant) => {
                    const selectedRecipeCount = getQuantitiesForRecipeId(recipe.id)
                    const selectedRecipePortionCount = getNumPortionsForRecipeId(recipe.id)
                    const props = {
                      recipeCount: selectedRecipeCount,
                      portionCount: selectedRecipePortionCount,
                      onEdit: () => {
                        trackClickEditRecipeQuantity({
                          basketRecipeCount: recipeCount,
                          menuUuid: basketMenuUuid,
                          recipePortionCount: selectedRecipePortionCount,
                          recipeUuid: recipe.id,
                          source: 'menu_search_screen',
                        })
                        openFlexPortionEditor(recipe, {
                          invocationId: context.invocationId,
                          menuId: basketMenuUuid,
                          index: context.rank,
                        })
                      },
                    }

                    return variant === 'desktop' ? (
                      <FlexPortionSummary
                        onEdit={props.onEdit}
                        portionCount={props.portionCount}
                        recipeCount={props.recipeCount}
                        stopEventPropagation
                      />
                    ) : (
                      <FlexPortionTileBadge
                        onEdit={props.onEdit}
                        portionCount={props.portionCount}
                        recipeCount={props.recipeCount}
                      />
                    )
                  }
                : undefined
            }
          />
        </SearchResultsRegion>
      </Box>
      {isFlexPortionEditorOpen && <FlexModalOverlay onClick={closeFlexPortionEditor} />}
      {flexPortionEditorRecipe && isFlexPortionEditorOpen && (
        <Box
          bottom="0"
          left="0"
          position={DisplayPosition.Absolute}
          right="0"
          width="100%"
          zIndex={ZIndex.Top}
        >
          <ModalFooter>
            <Box className={css.modalFooterContent}>
              <FlexPortionFooter
                recipeCount={flexPortionEditorRecipeCount}
                numPortions={flexPortionEditorNumPortions}
                onUpdatePortions={(updatedNumPortions) => {
                  updateFlexPortionEditorRecipePortions(updatedNumPortions)
                  if (flexPortionEditorTrackingContext) {
                    trackUpdatePortion(flexPortionEditorTrackingContext, updatedNumPortions)
                  }
                }}
                onSelectPortions={(numPortions) => {
                  if (flexPortionEditorTrackingContext) {
                    trackSelectPortionCount(flexPortionEditorTrackingContext, numPortions)
                  }
                }}
                onRemoveRecipe={removeFlexPortionEditorRecipe}
                mode="editor"
                onCloseEditor={closeFlexPortionEditor}
                renderEditor={({ onClose, onRemoveRecipe, onUpdatePortions }) => (
                  <FlexPortionEditorBoundary
                    // The endpoint requires mutually exclusive UUID/URN identifiers.
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...(flexPortionEditorRecipe.recipeUrn
                      ? { recipeUrn: flexPortionEditorRecipe.recipeUrn }
                      : { recipeId: flexPortionEditorRecipe.recipeId })}
                    deliveryDate={basketDate}
                    isOpen
                    menuId={basketMenuUuid}
                    numPortions={flexPortionEditorNumPortions}
                    onClose={onClose}
                    onRemoveRecipe={onRemoveRecipe}
                    onSelectPortions={(numPortions) => {
                      if (flexPortionEditorTrackingContext) {
                        trackSelectPortionCount(flexPortionEditorTrackingContext, numPortions)
                      }
                    }}
                    onUpdatePortions={onUpdatePortions}
                    recipeCount={flexPortionEditorRecipeCount}
                  />
                )}
              />
            </Box>
          </ModalFooter>
        </Box>
      )}
    </Modal>
  )
}
